import {
  DialogContent,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { z } from 'zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createCandidate, getCandidateReferrals, getCandidateAreas, isExistingEmail, isExistingPhone } from '../../../../Services/Candidates';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as paths from '../../../../Routing/paths';
import FormDialogContainer from '../../../FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../../DialogActionButtons/DialogActionButtons';
import { CandidateSchema } from '../../../../Utils/schemas';
import { assignCandidateToJob, CandidateSource } from '../../../../Services/Jobs';
import { useState } from 'react';
import { ReferralSourceOptions } from '../../../../Types/constants';
import { ContactSource } from '../../../../Views/Candidates/Components/CandidateTable/Components/ContactSource';

interface CandidateFormDialogProps {
  open: boolean;
  handleClose: () => void;
  jobId?: string;
  onInvalidateQuery?: () => void;
}

export default function CandidateFormDialog({
  open,
  handleClose,
  jobId,
  onInvalidateQuery,
}: CandidateFormDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [candidateSource, setCandidateSource] = useState<string>('LinkedIn Recruiter');
  const [sourceDetail, setSourceDetail] = useState<string>('');

  const nameLabel = t('form_fields.name.candidate_label');
  const emailLabel = t('form_fields.email.label');
  const phoneLabel = t('form_fields.phone.label');
  const linkedinLabel = t('form_fields.linkedin.label');

  const assignSuccessMessage = t('jobs.assign.success_message');
  const createCandidateSuccess = t('candidates.create.success_message');
  const createCandidateTitle = t('candidates.create.title');
  const createCandidateDescription = t('candidates.create.description');
  const createCandidateError = t('toast.errors.something_went_wrong');

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<z.infer<typeof CandidateSchema>>({
    resolver: zodResolver(CandidateSchema),
  });

  const { data: referralSources, isLoading: loadingReferralSources } = useQuery({
    queryKey: ['referralSources'],
    queryFn: getCandidateReferrals,
  });

  const { data: areaSources, isLoading: loadingAreaSources } = useQuery({
    queryKey: ['areaSources'],
    queryFn: getCandidateAreas,
  });

  const [referralSourceId, setReferralSourceId] = useState<string>('');
  const [referralDetail, setReferralDetail] = useState<string>('');
  const [formError, setFormError] = useState(false);
  const [areaSourceId, setAreaSourceId] = useState<string>('');

  const isOtherSelected = referralSourceId === ReferralSourceOptions.Other.toString();

  const { mutate: assignCandidateMutation } = useMutation({
    mutationFn: (data: CandidateSource) => assignCandidateToJob(jobId!, data),
    onError: () => toast.error(createCandidateError),
    onSuccess: () => {
      toast.success(assignSuccessMessage);
      handleClose();
      onInvalidateQuery?.();
    },
  });

  const { mutate: createCandidateMutation } = useMutation({
    mutationFn: (data: Candidate) => createCandidate(data),
    onError: () => toast.error(createCandidateError),
    onSuccess: (data: Candidate) => {
      toast.success(createCandidateSuccess);
      if (jobId) {
        const assignData: CandidateSource = {
          candidateId: '' + data.id,
          candidateSource: referralSourceId,
          sourceDetail: isOtherSelected ? referralDetail : undefined,
        };
        assignCandidateMutation(assignData);
      } else {
        handleClose();
        navigate(paths.CANDIDATES);
        onInvalidateQuery?.();
      }
    },
  });

  const onSubmit: SubmitHandler<z.infer<typeof CandidateSchema>> = async (data) => {
    if (!!jobId && (!referralSourceId || (isOtherSelected && referralDetail.trim() === ''))) {
      setFormError(true);
      return;
    }

    const newCandidate: Candidate = {
      fullName: data.fullName,
      phone: data.phone?.trim(),
      email: data.email?.trim(),
      linkedin: data.linkedin,
      referralId: referralSourceId ? Number(referralSourceId) : undefined,
      referralDetail: isOtherSelected ? referralDetail : undefined,
      areaId: areaSourceId ? Number(areaSourceId) : undefined,
      languageProficiencyData: {
        level: '',
        testDate: '',
        testResult: '',
        comments: '',
        otherLanguages: ''
      },
    };
    if (data.phone && await isExistingPhone(newCandidate.phone!)){
      toast.error('Phone already exists');
      return;
    }
    
    if (data.email && await isExistingEmail(newCandidate.email!)){
      toast.error('Email already exists');
      return;
    }

    createCandidateMutation(newCandidate);
    reset();
  };

  const referralOptions =
    referralSources?.map((r: { id: number; name: string }) => ({
      name: r.name,
      value: r.id.toString(),
    })) ?? [];

  const areaOptions =
    areaSources?.map((r: { id: number; name: string }) => ({
      name: r.name,
      value: r.id.toString(),
    })).sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)) ?? [];

  return (
    <FormDialogContainer open={open} handleClose={handleClose} title={createCandidateTitle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 0 }}>
          <DialogContentText>{createCandidateDescription}</DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            id="fullName"
            label={nameLabel}
            type="text"
            fullWidth
            variant="outlined"
            {...register('fullName')}
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
          />

          <TextField
            margin="dense"
            id="linkedin"
            label={linkedinLabel}
            type="text"
            fullWidth
            variant="outlined"
            {...register('linkedin')}
            error={!!errors.linkedin}
            helperText={errors.linkedin?.message}
          />

          <TextField
            margin="dense"
            id="phone"
            label={phoneLabel}
            type="text"
            fullWidth
            variant="outlined"
            {...register('phone')}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />

          <TextField
            margin="dense"
            id="email"
            label={emailLabel}
            type="email"
            fullWidth
            variant="outlined"
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <FormControl fullWidth margin="dense" disabled={loadingReferralSources}>
            <InputLabel id="referral-source-label">
              {t('candidate_jobs.referral_source')}
            </InputLabel>
            <Select
              labelId="referral-source-label"
              id="referral-source"
              value={referralSourceId}
              onChange={(e) => {
                setReferralSourceId(e.target.value);
                setFormError(false);
              }}
              label={t('candidate_jobs.referral_source')}
            >
              {referralOptions.map((option: { name: string; value: string }) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isOtherSelected && (
            <TextField
              required
              fullWidth
              multiline
              margin="dense"
              label={t('candidate_jobs.referral_detail')}
              value={referralDetail}
              onChange={(e) => setReferralDetail(e.target.value)}
              error={formError && referralDetail.trim() === ''}
              helperText={
                formError && referralDetail.trim() === '' ? t('common.required') : ''
              }
            />
          )}

          <FormControl fullWidth margin="dense" disabled={loadingAreaSources}>
            <InputLabel id="area-source-label">
              {t('candidate_jobs.area_source')}
            </InputLabel>
            <Select
              labelId="area-source-label"
              id="area-source"
              value={areaSourceId}
              {...register('areaId', {
                setValueAs: (v) => v ? Number(v) : 0
              })}
              error={!!errors.areaId}
              onChange={(e) => {
                setAreaSourceId(e.target.value);
              }}
              label={t('candidate_jobs.area_source')}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 200 },
                },
              }}
            >
              {areaOptions.map((option: { name: string; value: string }) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
              {errors.areaId?.message}
            </Typography>
          </FormControl>
          
          {!!jobId && (
            <ContactSource
              candidateSource={candidateSource}
              setSource={setCandidateSource}
              sourceDetail={sourceDetail}
              setSourceDetail={setSourceDetail}
            />
          )}

        </DialogContent>

        <DialogActionButtons
          onCancel={() => {
            handleClose();
            reset();
          }}
        />
      </form>
    </FormDialogContainer>
  );
}
